import { AfterViewInit, Component, DestroyRef, inject, Inject, NgZone, OnInit } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus, ServerError } from '@azure/msal-browser';
import { B2CPolicy, LoginErrorCode } from '@core/constants';
import { AccessibilityObserverService } from '@core/services/accessibility-observer.service';
import { NotificationService } from '@core/services/notification.service';
import { PlatformService } from '@core/services/platform.service';
import { ENV } from '@env/environment-token';
import { IEnvironmentVariables } from '@env/IEnvironment';
import { SecurityService } from '@mynexus/mynexus-ui-lib';
import { filter, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit {
  initComplete = false;
  authenticationService = inject(MsalService);
  constructor(
    private authService: MsalService,
    private securityService: SecurityService,
    private router: Router,
    private notificationService: NotificationService,
    private msalBroadcastService: MsalBroadcastService,
    private platformService: PlatformService,
    private accessibilityObserverService: AccessibilityObserverService,
    private ngZone: NgZone,
    private destroyRef: DestroyRef,
    @Inject(ENV) private envConfig: IEnvironmentVariables
  ) {}

  ngOnInit(): void {

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: any): void => {
      if (event instanceof NavigationStart) {
        this.notificationService.clear();
      }
    });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.checkAndSetActiveAccount());

    this.authService.handleRedirectObservable().subscribe({
      next: (authResult) => {
        // We need to reject id tokens that were not issued with the default sign-in policy.
        // "tfp" claim in the token tells us what policy is used
        if ((authResult?.idTokenClaims as any)?.['tfp'] === B2CPolicy.ForgotPassword) {
          this.authService.logout();
        }
        else if (this.authenticationService.instance?.getAllAccounts()[0]) {
          this.initializePermissions();
        }

      },
      error: (error) => {
        this.handleLoginError(error);
      },
    });
  }

  ngAfterViewInit(): void {
    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      this.accessibilityObserverService.start();
    });
  }

  private handleLoginError(error: ServerError): void {
    if (!error) {
      return;
    }

    if (error.errorMessage.includes(LoginErrorCode.ForgotPassword)) {
      this.authService.loginRedirect({
        authority: `${this.envConfig.authorityBaseUrl}/${B2CPolicy.ForgotPassword}`,
        scopes: this.envConfig.authConfig.angularConfig.consentScopes,
      });
    }

    if (error.errorMessage.includes(LoginErrorCode.CancelFlow)) {
      this.authService.loginRedirect({
        authority: `${this.envConfig.authorityBaseUrl}/${B2CPolicy.SignUpSignIn}`,
        scopes: this.envConfig.authConfig.angularConfig.consentScopes,
      });
    }

  }

  checkAndSetActiveAccount() {
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  initializePermissions(): void {

    this.platformService.getPermissions().subscribe({
      next: permissions => {
        this.securityService.setUserPermissions(
          Object.values(permissions).flat()
        );
        this.securityService.setAllowEmptyPermission(false);
        this.initComplete = true;
      },
    });
  }
}
